import React, { useEffect, useState } from "react";
import { Bell, Plus, Trash2, ChevronDown, ChevronRight } from "lucide-react";
import Toggle from "../ui/Toggle";
import Button from "../ui/Button";
import { billingAPI } from "../../services/api/billing";
import { useToast } from "../../contexts/ToastContext";

const TYPES = [
  { value: "credits_used_in_period", label: "Credits used this period" },
  { value: "balance_remaining",      label: "Balance remaining" },
  { value: "credits_used_absolute",  label: "Total credits used (lifetime)" },
];

const CHANNELS = [
  { value: "email",  label: "Email" },
  { value: "in_app", label: "In-app" },
  { value: "both",   label: "Both" },
];

const defaultForm = { threshold_type: "", threshold_value: "", channel: "email" };

export default function UsageAlertsCard() {
  const { showToast } = useToast();
  const [alerts, setAlerts] = useState([]);
  const [expanded, setExpanded] = useState(false);
  const [form, setForm] = useState(defaultForm);
  const [formError, setFormError] = useState("");
  const [confirmDelete, setConfirmDelete] = useState(null);

  const load = async () => {
    try { setAlerts(await billingAPI.listUsageAlerts()); }
    catch (e) { setAlerts([]); }
  };

  useEffect(() => { load(); }, []);

  const onChange = (field, val) => {
    setForm(prev => ({ ...prev, [field]: val }));
    setFormError("");
  };

  const onCreate = async (e) => {
    e.preventDefault();
    if (!form.threshold_type) { setFormError("Please choose a type."); return; }
    if (!form.threshold_value || Number(form.threshold_value) < 1) { setFormError("Enter a valid threshold value."); return; }
    try {
      await billingAPI.createUsageAlert({ ...form, threshold_value: Number(form.threshold_value) });
      setForm(defaultForm);
      await load();
      showToast("Alert added", "success");
    } catch (e) {
      showToast(e.response?.data?.error || "Failed to add alert", "error");
    }
  };

  const onToggle = async (row, enabled) => {
    try {
      await billingAPI.updateUsageAlert(row.id, { enabled });
      await load();
    } catch (e) {
      showToast("Failed to update", "error");
    }
  };

  const onDelete = async (id) => {
    try {
      await billingAPI.deleteUsageAlert(id);
      setConfirmDelete(null);
      await load();
    } catch (e) {
      showToast("Failed to delete", "error");
    }
  };

  return (
    <section>
      {/* Collapsed header — clickable row */}
      <button
        onClick={() => setExpanded(v => !v)}
        className="w-full flex items-center justify-between px-5 py-3.5 rounded-xl bg-card border border-border hover:border-border transition-colors text-left"
      >
        <div className="flex items-center gap-2.5">
          <div className="w-8 h-8 rounded-lg bg-muted/50 flex items-center justify-center">
            <Bell className="h-3.5 w-3.5 text-muted-foreground" />
          </div>
          <div>
            <div className="text-sm font-medium text-foreground">Usage alerts</div>
            <div className="text-xs text-muted-foreground">
              {alerts.length === 0
                ? "Get notified when credits hit a threshold"
                : `${alerts.length} alert${alerts.length === 1 ? "" : "s"} configured`}
            </div>
          </div>
        </div>
        {expanded ? <ChevronDown className="h-4 w-4 text-muted-foreground" /> : <ChevronRight className="h-4 w-4 text-muted-foreground" />}
      </button>

      {/* Expanded body */}
      {expanded && (
        <div className="mt-2 bg-card rounded-xl border border-border p-4 space-y-5">
          {/* Existing alerts */}
          {alerts.length > 0 && (
            <table className="w-full text-sm">
              <thead>
                <tr>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide pb-3 pr-4">Type</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide pb-3 pr-4">Threshold</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide pb-3 pr-4">Channel</th>
                  <th className="text-left text-xs font-medium text-muted-foreground uppercase tracking-wide pb-3 pr-4">Enabled</th>
                  <th className="pb-3" />
                </tr>
              </thead>
              <tbody>
                {alerts.map(row => (
                  <tr key={row.id} className="border-t border-gray-50 hover:bg-muted/50/50 transition-colors">
                    <td className="py-3 pr-4 text-foreground">
                      {TYPES.find(t => t.value === row.threshold_type)?.label || row.threshold_type}
                    </td>
                    <td className="py-3 pr-4 text-foreground tabular-nums">{row.threshold_value}</td>
                    <td className="py-3 pr-4 text-muted-foreground capitalize">{row.channel}</td>
                    <td className="py-3 pr-4">
                      <Toggle enabled={row.enabled} onToggle={() => onToggle(row, !row.enabled)} />
                    </td>
                    <td className="py-3 text-right">
                      {confirmDelete === row.id ? (
                        <div className="flex items-center justify-end gap-2">
                          <span className="text-xs text-muted-foreground">Delete?</span>
                          <button onClick={() => onDelete(row.id)} className="text-xs text-red-600 font-medium hover:underline">Yes</button>
                          <button onClick={() => setConfirmDelete(null)} className="text-xs text-muted-foreground hover:underline">No</button>
                        </div>
                      ) : (
                        <button onClick={() => setConfirmDelete(row.id)} className="p-1.5 rounded-lg hover:bg-red-50 text-muted-foreground hover:text-red-500 transition-colors" title="Delete alert">
                          <Trash2 className="h-4 w-4" />
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}

          {/* Add alert form */}
          <form onSubmit={onCreate} className={alerts.length > 0 ? "pt-4 border-t border-gray-50" : ""}>
            <div className="text-xs font-medium text-muted-foreground uppercase tracking-wide mb-3">Add alert</div>
            <div className="flex flex-wrap gap-3 items-end">
              <div className="flex-1 min-w-[220px]">
                <label className="block text-xs text-muted-foreground mb-1">Type</label>
                <select value={form.threshold_type} onChange={e => onChange("threshold_type", e.target.value)}
                  className="w-full border border-input rounded-md px-3 py-2 text-sm bg-card focus:outline-none focus:ring-2 focus:ring-primary/30">
                  <option value="">Select type…</option>
                  {TYPES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
                </select>
              </div>
              <div className="w-32">
                <label className="block text-xs text-muted-foreground mb-1">Value</label>
                <input type="number" min={1} placeholder="e.g. 500" value={form.threshold_value}
                  onChange={e => onChange("threshold_value", e.target.value)}
                  className="w-full border border-input rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/30" />
              </div>
              <div className="w-32">
                <label className="block text-xs text-muted-foreground mb-1">Channel</label>
                <select value={form.channel} onChange={e => onChange("channel", e.target.value)}
                  className="w-full border border-input rounded-md px-3 py-2 text-sm bg-card focus:outline-none focus:ring-2 focus:ring-primary/30">
                  {CHANNELS.map(c => <option key={c.value} value={c.value}>{c.label}</option>)}
                </select>
              </div>
              <Button variant="primary" type="submit">
                <Plus className="h-4 w-4" />
                <span>Add alert</span>
              </Button>
            </div>
            {formError && <p className="mt-2 text-xs text-red-500">{formError}</p>}
          </form>
        </div>
      )}
    </section>
  );
}
